import axios, { AxiosResponse } from 'axios';
import {
  Asset,
  BackendResponse,
  ForumItem,
  IAssetFeature,
  IAssetYearlyTaxReport,
  IAssetYearlyTaxReports,
  IDebugCsv,
  IForumVotings,
  IGermanTax,
  IGermanTaxByUserByAsset,
  IGetAPIResponseItem,
  IInvestorReportWrapper,
  IPostAPIResponseItem,
  IRecordGetResponse,
  IRecordPostPayload,
  IRecordPutPayload,
  ITaxReport,
  IUser,
  IUserReportWrapper,
  MangopayReadiness,
  Notary,
  PaginationResponse,
  Payout,
  PayoutPostRequest,
  Payouts,
  PostInstantVotingIssuerPayload,
  PostMessagePayload,
  PostNotaryPayload,
  PostPayout,
  PostVotingIssuerPayload,
  SPV,
  TPreorder,
  TRevenuePool,
} from '../models';
import { PayoutStatus } from '../models/enum';
import { REACT_APP_API_PLATFORM_URL } from '../util/config';
import { POST_WITH_ID } from '@brickwise/asset-validation/dist/asset/assetData';
import { TAssetInvestmentDetailsSchema } from '@brickwise/asset-validation/dist/asset/investmentData';
import { TCompleteAssetSchema } from '@brickwise/asset-validation/dist/schema/completeAsset';
import { DeepPartial } from '../models/deepPartial';

const api = axios.create({
  baseURL: `${REACT_APP_API_PLATFORM_URL}/launchpad/api`,
  withCredentials: true,
});

export const getAssets = async (): Promise<BackendResponse<Asset[]>> => {
  const { data } = await api.get<BackendResponse<Asset[]>>('/v2/assets/partner');
  return data;
};

export function copyAsset(assetId: string) {
  return api.post('/v1/asset/copy/', {
    copyFromAssetId: assetId,
  });
}

export const getAssetsLoggedOut = () => {
  return api.get<Asset[]>('/v2/assets?newFormat=true');
};

export const getAsset = (assetId: string) => {
  return api.get<TCompleteAssetSchema>(`/v2/asset/${assetId}?newFormat=true`);
};

export const getRevenuePool = (id: string): Promise<AxiosResponse<BackendResponse<TRevenuePool>>> => {
  return api.get(`/v1/revenuePools/${id}`);
};

export const getRevenuePoolAssets = (id: string): Promise<AxiosResponse<BackendResponse<{ items: Asset[]; count: number }>>> => {
  return api.get(`/v1/revenuePools/${id}/assets`);
};

export const getRevenuePools = (filters: {
  page?: number;
  rowsPerPage?: number;
  title?: string;
}): Promise<AxiosResponse<BackendResponse<{ items: TRevenuePool[]; count: number }>>> => {
  return api.get('/v1/revenuePools', { params: filters });
};

export const getPreorders = (
  assetId: string,
  filters: {
    page?: number;
    rowsPerPage?: number;
    title?: string;
  },
): Promise<AxiosResponse<BackendResponse<{ items: TPreorder[]; count: number }>>> => {
  return api.get(`/v1/asset/preorder/${assetId}/all`, { params: filters });
};

export const postRevenuePool = (data: TRevenuePool): Promise<AxiosResponse<BackendResponse<void>>> => {
  return api.post('/v1/revenuePools', data);
};

export const patchRevenuePool = (id: string, data: TRevenuePool): Promise<AxiosResponse<BackendResponse<void>>> => {
  return api.patch(`/v1/revenuePools/${id}`, data);
};

export const postNewAsset = (body: POST_WITH_ID): Promise<BackendResponse<{ data: { id: string } }>> => {
  return api.post('/v1/asset/assetData', body);
};

export const requestApproval = (assetId: string) => {
  return api.post(`/v1/asset/requestApproval/${assetId}`);
};

export const postPreorder = (assetId: string) => {
  return api.post(`/v1/asset/preorder/${assetId}`);
};

export const extendIssueDuration = (assetId: string, days: string) => {
  return api.post(`/v1/asset/${assetId}/extendIssueDuration/${days}`);
};

export const deleteAsset = (assetId: string) => {
  return api.delete(`/v1/asset/${assetId}`);
};

export const transferShares = async (assetId: string) => {
  const response = await api.get<TCompleteAssetSchema>(`/v2/asset/${assetId}?newFormat=true`);
  const asset = response.data;
  if (asset.type === 'builderOwnerModel' || asset.type === 'powerplantPortfolio' || asset.type === 'citizenParticipationLoan') {
    return api.post(`/v1/asset/emit-none-blockchain/${assetId}`);
  } else {
    return api.post(`/v1/asset/emit/${assetId}`);
  }
};

export const startSubscriptionPeriod = (assetId: string) => {
  return api.post(`/v1/asset/campaign/${assetId}`);
};

export async function getTransactionSerialNumbers({ transactionId, assetId }: { transactionId: string; assetId: string }) {
  const url = `/v1/asset/serialnumber/${assetId}?transactionId=${transactionId}`;
  return api.get<BackendResponse<{ userId: string; transactionId: string; serialNumber: string }[]>>(url);
}

export const postAssetUpdate = (assetId: string, update: POST_WITH_ID) => {
  return api.post(`/v1/asset/assetData/${assetId}`, update);
};

export const postAssetInvestmentDataUpdate = (assetId: string, update: TAssetInvestmentDetailsSchema) => {
  return api.post(`/v1/asset/investmentData/${assetId}`, update);
};

export const getAssetFeatures = () => {
  return api.get<BackendResponse<IAssetFeature[]>>('/v1/assetFeatures');
};

export const getSPVs = () => {
  return api.get<BackendResponse<SPV[]>>('/v1/spv/partner');
};

export const postSPVs = (body: DeepPartial<SPV>) => {
  return api.post<BackendResponse<SPV>>('/v1/spv', body);
};

export const putSPVs = (id: string, body: DeepPartial<SPV>) => {
  return api.put<BackendResponse<void>>(`/v1/spv/${id}`, body);
};

export const getSPV = (id: string) => {
  return api.get<BackendResponse<SPV>>(`/v1/spv/${id}`);
};

export const getPayoutsBySpv = (spvs: string[], status: PayoutStatus[]) => {
  return api.get<BackendResponse<Payouts>>(
    `/v1/asset/payout/getPayoutsBySPV?spvId=${JSON.stringify(spvs)}&status=${JSON.stringify(status)}`,
  );
};

export const postSpvScaRefresh = (spvId: string) => {
  return api.post<BackendResponse<MangopayReadiness>>(`/v1/spv/${spvId}/mangopay/refresh`);
};

export const postSpvScaConsent = (spvId: string) => {
  return api.post<BackendResponse<{ redirectUrl: string }>>(`/v1/spv/${spvId}/mangopay/scaConsent`);
};

export const postSpvEnrollSca = (spvId: string) => {
  return api.post<BackendResponse<{ redirectUrl: string | null }>>(`/v1/spv/${spvId}/mangopay/enrollSca`);
};

export const postNotary = (assetId: string, body: PostNotaryPayload) => {
  return api.post<BackendResponse<Notary>>(`/v1/asset/notary/${assetId}`, body);
};

export const postNotaryFile = (assetId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<BackendResponse<Notary>>(`/v1/asset/notary/${assetId}/file`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const postSerialNumberFile = (assetId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<BackendResponse<Notary>>(`/v1/asset/serialnumber/${assetId}/file`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const getAllSerialNumbers = (assetId: string, page: number, pageSize: number) => {
  return api.get<
    BackendResponse<{ data: { serialNumber: string; userId: string; user: { firstName: string; lastName: string; email: string } }[] }>
  >(`/v1/asset/serialnumber/all/${assetId}?page=${page}&pageSize=${pageSize}`);
};

export const getAllSerialNumbersCSV = (assetId: string) => {
  return api.get<BackendResponse<{ data: any; error: any }>>(`/v1/asset/serialnumber/report/${assetId}`);
};

export const generateSerialNumbers = (assetId: string) => {
  return api.post(`/v1/asset/serialnumber/${assetId}/auto`);
};

export const getNotary = (assetId: string) => {
  return api.get<BackendResponse<{ notary: Notary }>>(`/v1/asset/notary/${assetId}`);
};

export const getPayouts = (assetId: string) => {
  return api.get<BackendResponse<Payout[]>>(`/v1/asset/payout/${assetId}/lastPayments`);
};

export const getAssetForumMessages = (assetId: string) => {
  return api.get<BackendResponse<ForumItem[]>>(`/v1/forum/messages/${assetId}`);
};

export const getVoting = (assetId: string, votingId: string) => {
  return api.get<BackendResponse<IForumVotings>>(`/v1/forum/${assetId}/votings/${votingId}`);
};

export const postForumMessage = (body: PostMessagePayload) => {
  return api.post('/v1/forum/message', body);
};

export const postNewVoting = (body: PostVotingIssuerPayload) => {
  return api.post('/v1/forum/issuer/voting', body);
};

export const postNewInstantVoting = (body: PostInstantVotingIssuerPayload) => {
  return api.post('/v1/forum/issuer/instantVoting', body);
};

export const postVotingSuggestionToUpcoming = (votingId: string) => {
  return api.post(`/v1/forum/issuer/voting/${votingId}/upcomingvoting`);
};

export const postPayout = (assetId: string, body: PayoutPostRequest) => {
  return api.post<BackendResponse<PostPayout>>(`/v1/asset/payout/${assetId}/rentalPayout`, {
    assetId: assetId,
    ...body,
  });
};

export const deleteInitPayout = (payoutId: string) => {
  return api.delete(`/v1/asset/payout/${payoutId}`);
};

export const getTan = (payoutId: string) => {
  return api.get(`/v1/asset/payout/${payoutId}/smstan`);
};

export const postTan = (payoutId: string, tan: string) => {
  return api.post(`/v1/asset/payout/${payoutId}/smstan`, { smsTan: tan });
};

export const getTanSpvPayout = (spvId: string, withdrawalAmount: string) => {
  return api.get<BackendResponse<string>>(`/v1/spv/${spvId}/smstan/?withdrawalAmount=${withdrawalAmount}`);
};

export const getRecords = (assetId: string, offset: number, limit: number) => {
  return api.get<BackendResponse<PaginationResponse<IRecordGetResponse[]>>>(`/v1/records/${assetId}`, {
    params: {
      offset,
      limit,
    },
  });
};

export const postNewRecord = (body: IRecordPostPayload) => {
  return api.post('/v1/record', body);
};

export const putEditRecord = (recordId: string, body: IRecordPutPayload) => {
  return api.put(`/v1/record/${recordId}`, body);
};

export const deleteRecords = (recordIds: string[]) => {
  return api.delete(`/v1/records?ids=${JSON.stringify(recordIds)}`);
};

export const postSpvTan = (payoutId: string, tan: string) => {
  return api.post(`/v1/spv/${payoutId}/smsTan?smsTan=${tan}`);
};

export const getAssetYearlyTaxReport = (assetId: string, year?: string) => {
  return api.get<BackendResponse<IAssetYearlyTaxReports[]>>(`/v1/yearlyReports/${assetId}/${year ?? ''}`);
};

export const postAssetYearlyTaxReport = (assetId: string, year: string, body: IAssetYearlyTaxReport) => {
  return api.post(`/v1/yearlyReport/${assetId}/${year}`, body);
};

export const putAssetYearlyTaxReport = (assetId: string, year: string, body: IAssetYearlyTaxReport) => {
  return api.put(`/v1/yearlyReport/${assetId}/${year}`, body);
};

export const getUser = () => {
  return api.get<BackendResponse<IUser>>('/v1/user');
};

export const getSpvTaxInformation = (spvId: string) => {
  return api.get<BackendResponse<ITaxReport[]>>(`/v1/spv/${spvId}/taxInformation`);
};

export const getSpvTaxXML = (spvId: string, year: string, month: string) => {
  return api.get<BackendResponse<string>>(`/v1/spv/${spvId}/taxXMLReport/${year}/${month}/xml`);
};

export const getYearlyTaxFormVerf60 = (assetId: string, year: string) => {
  return api.get<BlobPart>(`/v1/yearlyReport/${assetId}/${year}/verf60/pdf`, { responseType: 'blob' });
};

export const getYearlyTaxFormE6B = (assetId: string, year: string) => {
  return api.get<BlobPart>(`/v1/yearlyReport/${assetId}/${year}/e6b/zip`, { responseType: 'blob' });
};
export const getCSVDebug = (assetId: string, year: string) => {
  return api.get<BackendResponse<IDebugCsv>>(`/v1/yearlyReport/${assetId}/${year}/e6b/debug`);
};

export const getGermanTaxCsvByUser = (spvId: string, year: string, month: string) => {
  return api.get<BackendResponse<IGermanTax[]>>(`/v1/spv/${spvId}/taxGermanyCsvByUser/${year}/${month}/`);
};
export const getGermanTaxCsvByUserByAsset = (spvId: string, year: string, month: string) => {
  return api.get<BackendResponse<IGermanTaxByUserByAsset>>(`/v1/spv/${spvId}/taxGermanyCsvByUserByAsset/${year}/${month}/`);
};

export const getInvestorReport = (spvId: string) => {
  return api.get<BackendResponse<IInvestorReportWrapper>>(`/v1/reporting/investors/${spvId}/`);
};

export const getUserReport = () => {
  return api.get<BackendResponse<IUserReportWrapper>>('/v1/reporting/users/');
};

export const getAPIKeys = () => {
  return api.get<BackendResponse<IGetAPIResponseItem[]>>('/v1/spv/apikey');
};

export const postAPIKey = ({ name, ipAddresses }: { name: string; ipAddresses: string[] }) => {
  return api.post<BackendResponse<IPostAPIResponseItem>>('/v1/spv/apikey', {
    name,
    ipAddresses,
  });
};

export const deleteAPIKey = (id: string) => {
  return api.delete<void>(`/v1/spv/apikey/${id}`);
};
